import React from "react";
import { whatsappUnitCost } from "@/domain/projection/engine";
import { formatArs, newProjectionId } from "@/domain/projection/format";
import type { ProjectionModel } from "@/domain/projection/types";
import { Button } from "@/presentation/components/common";
import { ProjectionNumberField, projectionFieldClass } from "./ProjectionNumberField";

type PricesEditorProps = {
  model: ProjectionModel;
  onChange: (model: ProjectionModel) => void;
};

export const PricesEditor: React.FC<PricesEditorProps> = ({ model, onChange }) => {
  const waUnit = whatsappUnitCost(model);

  return (
    <div className="space-y-4">
      <div className="panel-card p-4">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Planes Coofit</h3>
        <p className="mt-1 text-xs text-slate-500">
          Precio de 1 profesional / 1 consultorio. Lo extra se suma por encima.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <ProjectionNumberField
            label="Base 1P/1C"
            value={model.plans.basePrice}
            onChange={(basePrice) => onChange({ ...model, plans: { ...model.plans, basePrice } })}
            min={0}
            step={1000}
          />
          <ProjectionNumberField
            label="Avanzado 1P/1C"
            value={model.plans.avanzadoPrice}
            onChange={(avanzadoPrice) => onChange({ ...model, plans: { ...model.plans, avanzadoPrice } })}
            min={0}
            step={1000}
          />
          <ProjectionNumberField
            label="Profesional extra"
            value={model.plans.extraProfessional}
            onChange={(extraProfessional) => onChange({ ...model, plans: { ...model.plans, extraProfessional } })}
            min={0}
            step={1000}
          />
          <ProjectionNumberField
            label="Consultorio extra"
            value={model.plans.extraConsultorio}
            onChange={(extraConsultorio) => onChange({ ...model, plans: { ...model.plans, extraConsultorio } })}
            min={0}
            step={1000}
          />
        </div>
      </div>

      <div className="panel-card p-4">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Panel Clínicas</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <ProjectionNumberField
            label="Precio base"
            value={model.panelClinicas.basePrice}
            onChange={(basePrice) => onChange({ ...model, panelClinicas: { ...model.panelClinicas, basePrice } })}
            min={0}
            step={1000}
          />
          <ProjectionNumberField
            label="Clínicas incluidas"
            value={model.panelClinicas.includedClinics}
            onChange={(includedClinics) =>
              onChange({ ...model, panelClinicas: { ...model.panelClinicas, includedClinics } })
            }
            min={1}
            step={1}
          />
          <ProjectionNumberField
            label="Clínica extra"
            value={model.panelClinicas.extraClinicPrice}
            onChange={(extraClinicPrice) =>
              onChange({ ...model, panelClinicas: { ...model.panelClinicas, extraClinicPrice } })
            }
            min={0}
            step={1000}
          />
        </div>
      </div>

      <div className="panel-table-wrap">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Packs Conexión (WhatsApp)</h3>
            <p className="text-xs text-slate-500">
              Contribución = precio − cupo × costo Meta. El Excel solo mapeaba 200/500/1000/1500; acá entra cualquier cupo.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => {
              const id = newProjectionId("wa");
              onChange({
                ...model,
                connectionPacks: [
                  ...model.connectionPacks,
                  { id, quota: 0, price: 0, enabled: true },
                ],
                adoption: {
                  ...model.adoption,
                  connectionMix: [...model.adoption.connectionMix, { packId: id, weight: 0 }],
                },
              });
            }}
          >
            Agregar pack
          </Button>
        </div>
        <table className="min-w-full text-left text-sm">
          <thead className="panel-thead text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Activo</th>
              <th className="px-3 py-2">Cupo WA</th>
              <th className="px-3 py-2">Precio</th>
              <th className="px-3 py-2 text-right">Costo Meta máx.</th>
              <th className="px-3 py-2 text-right">Contribución</th>
              <th className="px-3 py-2 text-right">Base + pack</th>
              <th className="px-3 py-2 text-right">Avanzado + pack</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {model.connectionPacks.map((pack, index) => {
              const metaMax = pack.quota * waUnit;
              const contribution = pack.price - metaMax;
              return (
                <tr key={pack.id} className="border-t border-slate-100 dark:border-gray-700">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={pack.enabled}
                      onChange={(event) => {
                        const connectionPacks = model.connectionPacks.map((row) =>
                          row.id === pack.id ? { ...row, enabled: event.target.checked } : row,
                        );
                        onChange({ ...model, connectionPacks });
                      }}
                    />
                  </td>
                  <td className="px-3 py-2 w-28">
                    <input
                      type="number"
                      className={projectionFieldClass}
                      value={pack.quota}
                      onChange={(event) => {
                        const quota = Number(event.target.value) || 0;
                        onChange({
                          ...model,
                          connectionPacks: model.connectionPacks.map((row) =>
                            row.id === pack.id ? { ...row, quota } : row,
                          ),
                        });
                      }}
                    />
                  </td>
                  <td className="px-3 py-2 w-32">
                    <input
                      type="number"
                      className={projectionFieldClass}
                      value={pack.price}
                      onChange={(event) => {
                        const price = Number(event.target.value) || 0;
                        onChange({
                          ...model,
                          connectionPacks: model.connectionPacks.map((row) =>
                            row.id === pack.id ? { ...row, price } : row,
                          ),
                        });
                      }}
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatArs(metaMax)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${contribution >= 0 ? "text-teal-700 dark:text-teal-300" : "text-rose-600"}`}>
                    {formatArs(contribution)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatArs(model.plans.basePrice + pack.price)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatArs(model.plans.avanzadoPrice + pack.price)}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-xs font-medium text-rose-600"
                      onClick={() => {
                        const removed = model.connectionPacks[index];
                        onChange({
                          ...model,
                          connectionPacks: model.connectionPacks.filter((row) => row.id !== pack.id),
                          adoption: {
                            ...model.adoption,
                            connectionMix: model.adoption.connectionMix.filter((row) => row.packId !== removed.id),
                          },
                        });
                      }}
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="panel-table-wrap">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Packs Marketing</h3>
            <p className="text-xs text-slate-500">El Excel no los metía al simulador de adopción; acá sí, con mix propio.</p>
          </div>
          <Button
            size="sm"
            onClick={() =>
              onChange({
                ...model,
                marketingPacks: [
                  ...model.marketingPacks,
                  {
                    id: newProjectionId("mkt"),
                    name: "Nuevo pack",
                    emailsPerMonth: 0,
                    price: 0,
                    enabled: true,
                  },
                ],
              })
            }
          >
            Agregar pack
          </Button>
        </div>
        <table className="min-w-full text-left text-sm">
          <thead className="panel-thead text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Activo</th>
              <th className="px-3 py-2">Nombre</th>
              <th className="px-3 py-2">Emails / mes</th>
              <th className="px-3 py-2">Precio</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {model.marketingPacks.map((pack) => (
              <tr key={pack.id} className="border-t border-slate-100 dark:border-gray-700">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={pack.enabled}
                    onChange={(event) =>
                      onChange({
                        ...model,
                        marketingPacks: model.marketingPacks.map((row) =>
                          row.id === pack.id ? { ...row, enabled: event.target.checked } : row,
                        ),
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2 min-w-[12rem]">
                  <input
                    className={projectionFieldClass}
                    value={pack.name}
                    onChange={(event) =>
                      onChange({
                        ...model,
                        marketingPacks: model.marketingPacks.map((row) =>
                          row.id === pack.id ? { ...row, name: event.target.value } : row,
                        ),
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2 w-32">
                  <input
                    type="number"
                    className={projectionFieldClass}
                    value={pack.emailsPerMonth}
                    onChange={(event) =>
                      onChange({
                        ...model,
                        marketingPacks: model.marketingPacks.map((row) =>
                          row.id === pack.id ? { ...row, emailsPerMonth: Number(event.target.value) || 0 } : row,
                        ),
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2 w-32">
                  <input
                    type="number"
                    className={projectionFieldClass}
                    value={pack.price}
                    onChange={(event) =>
                      onChange({
                        ...model,
                        marketingPacks: model.marketingPacks.map((row) =>
                          row.id === pack.id ? { ...row, price: Number(event.target.value) || 0 } : row,
                        ),
                      })
                    }
                  />
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    className="text-xs font-medium text-rose-600"
                    onClick={() =>
                      onChange({
                        ...model,
                        marketingPacks: model.marketingPacks.filter((row) => row.id !== pack.id),
                        adoption: {
                          ...model.adoption,
                          marketingMix: model.adoption.marketingMix.filter((row) => row.packId !== pack.id),
                        },
                      })
                    }
                  >
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
